
import React from "react";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Shield, Clock, Cog, Terminal, Download, AlertTriangle, Lock, Loader2, Inbox } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiGet } from "@/services/apiClient";
import { useUserTimezone } from "@/hooks/useUserTimezone";

interface Activity {
  id: string;
  user: string;
  action: string;
  device: string;
  deviceId: string;
  /** Raw ISO timestamp from the audit log; rendered via the timezone-aware formatter. */
  timestamp: string;
  type: "admin" | "installation" | "security" | "update" | "alert" | "mdm";
  duration: string;
}


function inferActivityType(action: string): Activity['type'] {
  const a = (action || '').toLowerCase();
  if (a.includes('security') || a.includes('policy') || a.includes('compliance')) return 'security';
  if (a.includes('update') || a.includes('upgrade') || a.includes('patch')) return 'update';
  if (a.includes('install') || a.includes('deploy')) return 'installation';
  if (a.includes('alert') || a.includes('warning') || a.includes('threat')) return 'alert';
  if (a.includes('admin') || a.includes('role') || a.includes('user')) return 'admin';
  if (a.includes('mdm') || a.includes('enroll') || a.includes('command')) return 'mdm';
  return 'admin';
}

export function RecentActivities() {
  const { formatRelative } = useUserTimezone();
  const { data: activities = [], isLoading } = useQuery({
    queryKey: ['recent-activities'],
    queryFn: async () => {
      try {
        const res = await apiGet<any>('/api/v1/audit/recent', { includeTenantHeader: true });
        const list = res?.data || res || [];
        if (!Array.isArray(list)) return [];
        return list.slice(0, 8).map((a: any, i: number) => ({
          id: a.id || a.auditId || String(i),
          user: a.user || a.username || a.actor || 'System',
          action: a.action || a.description || a.message || 'performed an action',
          device: a.device || a.deviceName || a.target || a.resource || '',
          deviceId: a.deviceId || '',
          timestamp: a.timestamp || a.createdAt || a.created_at || '',
          type: inferActivityType(a.action || a.description || ''),
          duration: a.duration || a.details || '',
        })) as Activity[];
      } catch {
        // API not available — return empty
        return [];
      }
    },
    staleTime: 60000,
    retry: 1,
  });

  // Function to render the appropriate icon based on activity type
  const renderIcon = (type: string) => {
    switch (type) {
      case "admin":
        return <Shield className="h-5 w-5 text-info" />;
      case "installation":
        return <Download className="h-5 w-5 text-success" />;
      case "security":
        return <Lock className="h-5 w-5 text-purple-600" />;
      case "update":
        return <Download className="h-5 w-5 text-amber-600" />;
      case "alert":
        return <AlertTriangle className="h-5 w-5 text-destructive" />;
      case "mdm":
        return <Terminal className="h-5 w-5 text-gray-600" />;
      default:
        return <Cog className="h-5 w-5 text-gray-600" />;
    }
  };

  // Function to determine details text based on activity type
  const getDetailsText = (activity: Activity) => {
    if (activity.duration) return activity.duration;
    switch (activity.type) {
      case "admin":
        return "Administrative action";
      case "installation":
        return "Software deployment";
      case "security":
        return "Security operation";
      case "update":
        return "System update";
      case "alert":
        return "Security alert - requires attention";
      case "mdm":
        return "MDM operation";
      default:
        return "";
    }
  };

  return (
    <GlassCard variant="default">
      <GlassCardHeader className="pb-2">
        <GlassCardTitle className="text-lg font-medium">Recent Activities</GlassCardTitle>
      </GlassCardHeader>
      <GlassCardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
            <Inbox className="h-8 w-8 mb-2 opacity-50" />
            <p className="text-sm">No recent activities</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {activities.map((activity) => (
              <li key={activity.id} className="flex items-start justify-between py-2">
                <div className="flex items-start space-x-4">
                  <div className="mt-0.5">
                    {renderIcon(activity.type)}
                  </div>
                  <div>
                    <p className="font-medium text-sm text-foreground">
                      {`${activity.user} ${activity.action}${activity.device ? ` on ${activity.device}` : ''}`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {getDetailsText(activity)}
                    </p>
                  </div>
                </div>
                <div className="text-xs text-muted-foreground" title={activity.timestamp || undefined}>
                  {activity.timestamp ? formatRelative(activity.timestamp) : ''}
                </div>
              </li>
            ))}
          </ul>
        )}
      </GlassCardContent>
    </GlassCard>
  );
}
